import { useEffect, useRef, useState } from 'react';
import { confirmarLeitura, FORMATOS_ACEITOS, leituraValida } from '../lib/scanner';

// A câmera do celular como leitor de código de barras — o gesto do aplicativo
// de banco, aplicado ao balcão.
//
// Existe para o caso em que o leitor USB não está por perto: conferência de
// prateleira, recebimento de mercadoria no meio da loja, ou simplesmente o
// vendedor com o celular na mão. O código lido entra no MESMO caminho do bip:
// preenche a busca e, na tela de Venda, a peça cai sozinha no carrinho.
//
// TRÊS CUIDADOS QUE NÃO SÃO OPCIONAIS:
//   * PARAR A CÂMERA. Track esquecido ligado deixa a luz do celular acesa e o
//     aparelho esquentando — o usuário culpa "o sistema", com razão.
//   * CONFIRMAR EM DOIS QUADROS. Um quadro tremido lê errado, e ler errado na
//     Venda põe a peça errada no carrinho (ver scanner.ts).
//   * SOLTAR A CÂMERA AO TROCAR DE ABA. `visibilitychange` fecha o leitor: o
//     navegador do celular pode congelar o vídeo e voltar com quadro velho.

export default function LeitorCodigo({ onLeitura, onFechar }: {
  onLeitura: (codigo: string) => void;
  onFechar: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const trilha = useRef<MediaStream | null>(null);
  const ultima = useRef<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let timer: number | undefined;

    const parar = () => {
      vivo = false;
      if (timer) window.clearInterval(timer);
      trilha.current?.getTracks().forEach((t) => t.stop());
      trilha.current = null;
    };

    (async () => {
      try {
        // `environment` é a câmera de trás — a da frente não alcança a prateleira.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
        });
        if (!vivo) { stream.getTracks().forEach((t) => t.stop()); return; }
        trilha.current = stream;
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play().catch(() => undefined);
        }

        const Detector = globalThis.BarcodeDetector!;
        const detector = new Detector({ formats: [...FORMATOS_ACEITOS] });

        timer = window.setInterval(async () => {
          if (!vivo || !video.current || video.current.readyState < 2) return;
          try {
            const achados = await detector.detect(video.current);
            const bruto = achados[0]?.rawValue;
            if (!bruto) return;

            const codigo = leituraValida(bruto);
            if (!codigo) return;

            if (confirmarLeitura(ultima.current, codigo)) {
              // Vibra para confirmar sem o vendedor precisar olhar a tela —
              // ele está com a peça na outra mão.
              navigator.vibrate?.(80);
              parar();
              onLeitura(codigo);
            } else {
              ultima.current = codigo;
            }
          } catch {
            // Quadro ruim acontece o tempo todo; não é erro para mostrar.
          }
        }, 150);
      } catch (e) {
        if (!vivo) return;
        const nome = (e as { name?: string }).name;
        setErro(
          nome === 'NotAllowedError'
            ? 'Permissão de câmera negada. Libere nas configurações do navegador e tente de novo.'
            : nome === 'NotFoundError'
              ? 'Nenhuma câmera encontrada neste aparelho.'
              : 'Não foi possível abrir a câmera.',
        );
      }
    })();

    const aoEsconder = () => { if (document.hidden) { parar(); onFechar(); } };
    document.addEventListener('visibilitychange', aoEsconder);

    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoEsconder);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
        <div className="flex items-center justify-between pb-3">
          <p className="font-bold">Aponte para o código</p>
          <button type="button" onClick={onFechar}
                  className="border border-edge px-3 py-2 text-sm">
            Fechar
          </button>
        </div>

        {erro ? (
          <div className="cartao p-4 text-sm text-alerta">{erro}</div>
        ) : (
          <div className="relative flex-1 overflow-hidden border border-edge bg-black">
            <video ref={video} playsInline muted
                   className="h-full w-full object-cover" />
            {/* A mira não lê nada — é para a pessoa saber onde encostar. */}
            <div className="pointer-events-none absolute inset-x-6 top-1/2 h-24 -translate-y-1/2 border-2 border-signal/80" />
          </div>
        )}

        <p className="pt-3 text-center text-xs text-dim">
          Funciona com o código da nossa etiqueta e com o de barras de fábrica.
          Segure firme por um instante — a leitura confirma sozinha.
        </p>
      </div>
    </div>
  );
}
